import renderTemplate from "@pch/nunjucks-extensions";

/**
 * Basic utility view for modals
 *
 * @usage
 *   var m = new ModalView(myElement, () => { console.log("closed"); }, () => { myElement.removeClass("shown"); });
 */
class ModalView {
  /**
   * @param HTMLElement|jQuery|Zepto The root element
   * @param object Addiotnal options
   *   onCloseCallback {function} Optional callback when the modal is closed
   *   closeHandlder {function} Optional function to handle the closing, defaults to .hide()
   *   showClass {string} Classname to add to the root when show() is called
   */
  constructor(rootNode, opts) {
    this.rootNode = $(rootNode);
    this.opts = opts || {};
    this.callback = this.opts.onCloseCallback;
    this.closeHandler = this.opts.closeHandler;

    this.rootNode.on(
      "click",
      '.c-overlay--dismissable, [data-dismiss="modal"]',
      this.onDismiss.bind(this)
    );
  }

  onDismiss(ev) {
    ev.preventDefault();
    this.close();
  }

  show() {
    if (this.opts.showClass) {
      this.rootNode.addClass(this.opts.showClass);
    }
  }

  close() {
    if (this.closeHandler) {
      this.closeHandler();
    } else if (this.opts.showClass) {
      this.rootNode.removeClass(this.opts.showClass);
    } else {
      this.rootNode.hide();
    }

    if (this.callback) {
      this.callback();
    }
  }

  destroy(removeElement) {
    this.rootNode.off("click");
    if (removeElement) {
      this.rootNode.remove();
    }
    this.rootNode = null;
    this.callback = null;
    this.closeHandler = null;
  }

  /**
   * Static factory for creating and showing a modal
   * Will destroy itself upon close unless you override by passing
   * your own close handler
   *
   * @param HTMLElement|jQuery|Zepto selector
   * @param object Additional options
   *   modalClass string extra css classname to apply to the root modal element
   *   closeHandler function Override by passing your own handler, or else the modal will be hidden then destroyed when dismissed
   *   onCloseCallback function Callback when the modal is closed
   */
  static createModal(bodyContent, config) {
    config = config || {};
    let modal;
    let tpl = $(renderTemplate("shared/generic-modal.html", config || {}));
    tpl.find(".c-card__body").html(bodyContent);
    tpl.appendTo(document.body);
    let closeHandler =
      config.closeHandler ||
      function() {
        tpl.hide();
        modal.destroy(true);
      };
    modal = new ModalView(tpl, {
      onCloseCallback: config.onCloseCallback,
      closeHandler: closeHandler
    });
    return modal;
  }

  /**
   * Static factory for creating a single image modal
   * Will destroy itself upon close unless you override by passing
   * your own close handler
   *
   * @param string|Image Url string or Image element
   * @param object Additional options
   *   modalClass string extra css classname to apply to the root modal element
   *   closeHandler function Override by passing your own handler, or else the modal will be hidden then destroyed when dismissed
   *   onCloseCallback function Callback when the modal is closed
   */
  static createImageModal(img, config) {
    if (typeof img === "string") {
      img = $("<img src='" + img + "'/>");
    }
    return ModalView.createModal(img, config);
  }

  /**
   * Static factory for creating and showing a generic error modal
   * Will destroy itself upon close
   *
   * @param function optional callback when the modal is closed
   */
  static createGenericErrorModal(callback) {
    let modal;
    let tpl = $(renderTemplate("shared/generic-error.html", {}));
    tpl.appendTo(document.body);
    let closeHandler = function() {
      tpl.hide();
      modal.destroy(true);
    };
    modal = new ModalView(tpl, {
      onCloseCallback: callback,
      closeHandler: closeHandler
    });
    return modal;
  }
}

export default ModalView;
